import { FormControl, FormErrorMessage, Text } from '@chakra-ui/react';
import classNames from 'classnames';
import { useEffect, useId, useMemo, useState } from 'react';
import NumberFormat, {
  type NumberFormatProps,
  type NumberFormatValues,
} from 'react-number-format';
import { useLocation } from 'react-router-dom';
import type { SingleValue } from 'react-select';

import { Input, type InputProps, Select } from '@revelio/ui';

import { SelectionListControls } from '../../../../../shared/selection-lists/components/SelectionListControls/SelectionListControls';
import { useCurrencyPreference } from '../../../../../shared/user-preferences/hooks/useCurrencyPreference';
import { shouldShowCurrentPreviousToggle } from '../../config/currentPreviousConfig';
import { PAY_FILTER_MAX } from '../../definitions/pay';
import { useTrackFilterReset } from '../../hooks/useTrackFilterReset';
import { FilterId, type RangeFilter } from '../../types/filters';
import styles from '../filter-overlay-select.module.css';
import { ToggleSelect } from '../ToggleSelect';
import layoutStyles from './range-filter-form.module.css';

interface OperatorOption {
  value: 'between' | 'greater_than' | 'less_than' | 'not_between';
  label: string;
}

interface RangeFilterFormProps {
  filter: RangeFilter;
  closeMenu: () => void;
  onSave: (filter: RangeFilter) => void;
  showNotToggle?: boolean;
}

const baseOperatorOptions: [OperatorOption, OperatorOption, OperatorOption] = [
  { value: 'between', label: 'is between' },
  { value: 'greater_than', label: 'is greater than' },
  { value: 'less_than', label: 'is less than' },
];

const notBetweenOption: OperatorOption = {
  value: 'not_between',
  label: 'is not between',
};

const getOperatorOptions = (showNotToggle: boolean): OperatorOption[] => {
  if (showNotToggle) {
    return [...baseOperatorOptions, notBetweenOption];
  }
  return [...baseOperatorOptions];
};

const getOptionOrFallback = (
  options: OperatorOption[],
  fallback: OperatorOption
): OperatorOption => {
  return options.find((option) => option.value === fallback.value) ?? fallback;
};

const isPayDefaultMaxSentinel = (
  max: number | undefined,
  isUserSubmitted: boolean | undefined
): boolean => {
  if (isUserSubmitted === true) {
    return false;
  }
  if (max === undefined) {
    return true;
  }
  return max === PAY_FILTER_MAX;
};

const getInitialOperator = (
  min: number | undefined,
  max: number | undefined,
  isNot: boolean | undefined,
  showNotToggle: boolean
): OperatorOption => {
  const options = getOperatorOptions(showNotToggle);
  const betweenOption = getOptionOrFallback(options, baseOperatorOptions[0]);
  const greaterThanOption = getOptionOrFallback(
    options,
    baseOperatorOptions[1]
  );
  const lessThanOption = getOptionOrFallback(options, baseOperatorOptions[2]);

  if (min !== undefined && max !== undefined) {
    if (isNot && showNotToggle) {
      return notBetweenOption;
    }
    return betweenOption;
  } else if (min !== undefined) {
    return greaterThanOption;
  } else if (max !== undefined) {
    return lessThanOption;
  }
  return betweenOption;
};

export const RangeFilterForm = ({
  closeMenu,
  filter,
  onSave,
  showNotToggle = false,
}: RangeFilterFormProps) => {
  const location = useLocation();
  const { currencySymbol, exchangeRate } = useCurrencyPreference();
  const trackFilterReset = useTrackFilterReset();
  const isPayFilter = filter.id === FilterId.PAY;
  const isFundingFilter =
    filter.id === FilterId.LAST_FUNDING_AMOUNT ||
    filter.id === FilterId.TOTAL_FUNDING_AMOUNT;
  const maxPayValue = Math.round(PAY_FILTER_MAX * exchangeRate);
  const maxPayLabel = `${currencySymbol}${maxPayValue.toLocaleString()}`;
  const operatorOptions = useMemo(
    () => getOperatorOptions(showNotToggle),
    [showNotToggle]
  );

  // Initialize from filter prop directly (same strategy as StaticListFilterForm and TreeFilterForm)
  const [operator, setOperator] = useState<OperatorOption>(() =>
    getInitialOperator(filter.min, filter.max, filter.isNot, showNotToggle)
  );
  const [startValue, setStartValue] = useState<number | undefined>(() => {
    if (isPayFilter && filter.min !== undefined) {
      // Convert from USD to user's currency for display, rounded to whole number
      return Math.round(filter.min * exchangeRate);
    }
    if (isFundingFilter && filter.min !== undefined) {
      // Convert from USD to user's currency for display, allow decimals
      return filter.min * exchangeRate;
    }
    return filter.min;
  });
  const [endValue, setEndValue] = useState<number | undefined>(() => {
    if (isPayFilter) {
      if (isPayDefaultMaxSentinel(filter.max, filter.isUserSubmitted)) {
        // Default to 100,000 in user's currency
        return 100000;
      }
      if (filter.max === undefined) {
        return undefined;
      }
      // Convert from USD to user's currency for display, rounded to whole number
      return Math.round(filter.max * exchangeRate);
    }
    if (isFundingFilter && filter.max !== undefined) {
      // Convert from USD to user's currency for display, allow decimals
      return filter.max * exchangeRate;
    }
    return filter.max;
  });
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [isCurrent, setIsCurrent] = useState<boolean>(filter.isCurrent ?? true);
  const [showErrors, setShowErrors] = useState(false);

  // Sync state from filter prop when it changes (same pattern as StaticListFilterForm)
  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const newOperator = getInitialOperator(
      filter.min,
      filter.max,
      filter.isNot,
      showNotToggle
    );
    if (operator.value !== newOperator.value) {
      setOperator(newOperator);
    }

    const newStartValue = (() => {
      if (isPayFilter && filter.min !== undefined) {
        return Math.round(filter.min * exchangeRate);
      }
      if (isFundingFilter && filter.min !== undefined) {
        return filter.min * exchangeRate;
      }
      return filter.min;
    })();
    if (startValue !== newStartValue) {
      setStartValue(newStartValue);
    }

    const newEndValue = (() => {
      if (isPayFilter) {
        if (isPayDefaultMaxSentinel(filter.max, filter.isUserSubmitted)) {
          return 100000;
        }
        if (filter.max === undefined) {
          return undefined;
        }
        return Math.round(filter.max * exchangeRate);
      }
      if (isFundingFilter && filter.max !== undefined) {
        return filter.max * exchangeRate;
      }
      return filter.max;
    })();
    if (endValue !== newEndValue) {
      setEndValue(newEndValue);
    }

    if (isCurrent !== (filter.isCurrent ?? true)) {
      setIsCurrent(filter.isCurrent ?? true);
    }
  }, [
    filter.min,
    filter.max,
    filter.isCurrent,
    filter.isUserSubmitted,
    exchangeRate,
  ]);

  useEffect(() => {
    const errors: string[] = [];

    // Validate that values are finite numbers
    // biome-ignore lint/suspicious/noGlobalIsFinite: migrated from develop
    if (startValue !== undefined && !isFinite(startValue)) {
      errors.push('Start value must be a valid number');
    }

    // biome-ignore lint/suspicious/noGlobalIsFinite: migrated from develop
    if (endValue !== undefined && !isFinite(endValue)) {
      errors.push('End value must be a valid number');
    }

    // Graduation year validation
    if (filter.id === FilterId.GRADUATION_YEAR) {
      if (
        startValue !== undefined &&
        // biome-ignore lint/suspicious/noGlobalIsFinite: migrated from develop
        isFinite(startValue) &&
        (startValue < 1950 || startValue > 2100)
      ) {
        errors.push('Start year must be between 1950 and 2100');
      }
      if (
        endValue !== undefined &&
        // biome-ignore lint/suspicious/noGlobalIsFinite: migrated from develop
        isFinite(endValue) &&
        (endValue < 1950 || endValue > 2100)
      ) {
        errors.push('End year must be between 1950 and 2100');
      }
    }

    // Last funding year validation
    if (filter.id === FilterId.LAST_FUNDING_YEAR) {
      if (
        startValue !== undefined &&
        // biome-ignore lint/suspicious/noGlobalIsFinite: migrated from develop
        isFinite(startValue) &&
        (startValue < 1000 || startValue > 9999)
      ) {
        errors.push('Start year must be a 4-digit number');
      }
      if (
        endValue !== undefined &&
        // biome-ignore lint/suspicious/noGlobalIsFinite: migrated from develop
        isFinite(endValue) &&
        (endValue < 1000 || endValue > 9999)
      ) {
        errors.push('End year must be a 4-digit number');
      }
    }

    // Year founded validation
    if (filter.id === FilterId.YEAR_FOUNDED) {
      if (
        startValue !== undefined &&
        // biome-ignore lint/suspicious/noGlobalIsFinite: migrated from develop
        isFinite(startValue) &&
        (startValue < 1000 || startValue > 9999)
      ) {
        errors.push('Start year must be a 4-digit number');
      }
      if (
        endValue !== undefined &&
        // biome-ignore lint/suspicious/noGlobalIsFinite: migrated from develop
        isFinite(endValue) &&
        (endValue < 1000 || endValue > 9999)
      ) {
        errors.push('End year must be a 4-digit number');
      }
    }

    // Years of experience validation
    if (filter.id === FilterId.YEARS_OF_EXPERIENCE) {
      if (
        startValue !== undefined &&
        // biome-ignore lint/suspicious/noGlobalIsFinite: migrated from develop
        isFinite(startValue) &&
        (startValue < 0 || startValue > 100)
      ) {
        errors.push('Start years must be between 0 and 100');
      }
      if (
        endValue !== undefined &&
        // biome-ignore lint/suspicious/noGlobalIsFinite: migrated from develop
        isFinite(endValue) &&
        (endValue < 0 || endValue > 100)
      ) {
        errors.push('End years must be between 0 and 100');
      }
    }

    // Funding filter validation - no negative amounts
    if (isFundingFilter) {
      // biome-ignore lint/suspicious/noGlobalIsFinite: migrated from develop
      if (startValue !== undefined && isFinite(startValue) && startValue < 0) {
        errors.push('Funding amount cannot be negative');
      }
      // biome-ignore lint/suspicious/noGlobalIsFinite: migrated from develop
      if (endValue !== undefined && isFinite(endValue) && endValue < 0) {
        errors.push('Funding amount cannot be negative');
      }
    }

    // Pay filter validation - no negative amounts
    if (isPayFilter) {
      // biome-ignore lint/suspicious/noGlobalIsFinite: migrated from develop
      if (startValue !== undefined && isFinite(startValue) && startValue < 0) {
        errors.push('Salary cannot be negative');
      }
      // biome-ignore lint/suspicious/noGlobalIsFinite: migrated from develop
      if (endValue !== undefined && isFinite(endValue) && endValue < 0) {
        errors.push('Salary cannot be negative');
      }
      if (
        (startValue !== undefined &&
          // biome-ignore lint/suspicious/noGlobalIsFinite: migrated from develop
          isFinite(startValue) &&
          startValue > maxPayValue) ||
        // biome-ignore lint/suspicious/noGlobalIsFinite: migrated from develop
        (endValue !== undefined && isFinite(endValue) && endValue > maxPayValue)
      ) {
        errors.push(`Salary cannot exceed ${maxPayLabel}`);
      }
    }

    // Range validation - start must not exceed end
    if (
      (operator.value === 'between' || operator.value === 'not_between') &&
      startValue !== undefined &&
      endValue !== undefined &&
      // biome-ignore lint/suspicious/noGlobalIsFinite: migrated from develop
      isFinite(startValue) &&
      // biome-ignore lint/suspicious/noGlobalIsFinite: migrated from develop
      isFinite(endValue) &&
      startValue > endValue
    ) {
      errors.push('Start value cannot be greater than end value');
    }

    setErrorMessages(errors);
  }, [
    startValue,
    endValue,
    filter.id,
    operator.value,
    isFundingFilter,
    isPayFilter,
    maxPayLabel,
    maxPayValue,
  ]);

  const handleAddFilter = () => {
    const { min, max, isNot } = (() => {
      switch (operator.value) {
        case 'between':
          return { min: startValue, max: endValue, isNot: false };
        case 'not_between':
          return { min: startValue, max: endValue, isNot: true };
        case 'greater_than':
          return { min: startValue, max: undefined, isNot: false };
        case 'less_than':
          return { min: undefined, max: endValue, isNot: false };
        default:
          return { min: startValue, max: endValue, isNot: false };
      }
    })();

    // Convert from user's currency to USD for storage
    const convertedMin = (() => {
      if (isPayFilter && min !== undefined) {
        return Math.round(min / exchangeRate);
      }
      if (isFundingFilter && min !== undefined) {
        return min / exchangeRate;
      }
      return min;
    })();
    const convertedMax = (() => {
      if (isPayFilter && max !== undefined) {
        return Math.round(max / exchangeRate);
      }
      if (isFundingFilter && max !== undefined) {
        return max / exchangeRate;
      }
      return max;
    })();

    // Use onSave prop (same strategy as StaticListFilterForm and TreeFilterForm)
    // This handles multi-filter instance creation via FilterFormRender
    onSave({
      ...filter,
      min: convertedMin,
      max: convertedMax,
      isActive: true,
      isNot,
      ...(filter.id === FilterId.PAY && { isCurrent }),
    });
    closeMenu();
  };

  const handleClearSelections = () => {
    trackFilterReset(filter, {
      selectionCountBefore:
        startValue !== undefined || endValue !== undefined ? 1 : 0,
    });
    setOperator(getOptionOrFallback(operatorOptions, baseOperatorOptions[0])); // Reset to 'between'
    setStartValue(undefined);
    setEndValue(undefined);
    setErrorMessages([]);
    setShowErrors(false);
  };

  const sharedNumberFormatProps = (() => {
    if (
      filter.id === FilterId.GRADUATION_YEAR ||
      filter.id === FilterId.LAST_FUNDING_YEAR ||
      filter.id === FilterId.YEAR_FOUNDED
    ) {
      return {
        thousandSeparator: false,
        format: '####',
        mask: '_',
      };
    }
    if (isPayFilter) {
      return {
        thousandSeparator: true,
        prefix: currencySymbol,
        decimalScale: 0,
      };
    }
    if (isFundingFilter) {
      return {
        thousandSeparator: true,
        prefix: currencySymbol,
        decimalScale: 2,
        allowNegative: false,
      };
    }
    return { thousandSeparator: true };
  })();

  const handleOperatorChange = (value: SingleValue<OperatorOption>) => {
    const nextOperator: OperatorOption =
      value ?? getOptionOrFallback(operatorOptions, baseOperatorOptions[0]);
    setOperator(nextOperator);
    setStartValue(undefined);
    setEndValue(undefined);
    setErrorMessages([]);
    setShowErrors(false);
  };

  const isInvalid = showErrors && errorMessages.length > 0;

  const errorId = useId();

  const isAddDisabled = (() => {
    if (operator.value === 'greater_than' && startValue === undefined) {
      return true;
    }
    if (operator.value === 'less_than' && endValue === undefined) {
      return true;
    }
    if (
      (operator.value === 'between' || operator.value === 'not_between') &&
      (startValue === undefined || endValue === undefined)
    ) {
      return true;
    }
    return false;
  })();

  return (
    <div className={layoutStyles.form}>
      <div className={layoutStyles.fields}>
        <div data-testid="range-operator-select">
          <Select
            options={operatorOptions}
            value={operator}
            onChange={handleOperatorChange}
            size="sm"
            isSearchable={false}
            className={classNames(styles.overlaySelect, styles.operatorSelect)}
            classNamePrefix="filter-overlay-select"
          />
        </div>

        <FormControl isInvalid={isInvalid}>
          {operator.value === 'between' || operator.value === 'not_between' ? (
            <div className={layoutStyles.inputRow}>
              <NumberInput
                isInvalid={isInvalid}
                aria-describedby={isInvalid ? errorId : undefined}
                {...sharedNumberFormatProps}
                value={startValue}
                onValueChange={(values: NumberFormatValues) => {
                  setStartValue(values.floatValue ?? undefined);
                  setShowErrors(false);
                }}
                data-testid="range-num-input-start"
              />
              <Text>to</Text>
              <NumberInput
                isInvalid={isInvalid}
                aria-describedby={isInvalid ? errorId : undefined}
                {...sharedNumberFormatProps}
                value={endValue}
                onValueChange={(values: NumberFormatValues) => {
                  setEndValue(values.floatValue ?? undefined);
                  setShowErrors(false);
                }}
                data-testid="range-num-input-end"
              />
            </div>
          ) : (
            <NumberInput
              isInvalid={isInvalid}
              aria-describedby={isInvalid ? errorId : undefined}
              key={`single-${operator.value}`}
              {...sharedNumberFormatProps}
              value={operator.value === 'greater_than' ? startValue : endValue}
              onValueChange={
                operator.value === 'greater_than'
                  ? (values: NumberFormatValues) => {
                      setStartValue(values.floatValue ?? undefined);
                      setShowErrors(false);
                    }
                  : (values: NumberFormatValues) => {
                      setEndValue(values.floatValue ?? undefined);
                      setShowErrors(false);
                    }
              }
              data-testid={
                operator.value === 'greater_than'
                  ? 'range-num-input-start'
                  : 'range-num-input-end'
              }
            />
          )}

          <FormErrorMessage id={errorId}>
            <div className={layoutStyles.errorList}>
              {errorMessages.map((msg, idx) => (
                <Text key={idx} color="red.600">
                  {msg}
                </Text>
              ))}
            </div>
          </FormErrorMessage>
        </FormControl>

        {shouldShowCurrentPreviousToggle(filter.id, location.pathname) && (
          <ToggleSelect
            filterName={filter.name}
            onChange={(e: number) => setIsCurrent(e === 0)}
            initialIsCurrent={isCurrent}
          />
        )}
      </div>

      <SelectionListControls
        onClear={handleClearSelections}
        onClose={closeMenu}
        onSubmit={() => {
          if (errorMessages.length > 0) {
            setShowErrors(true);
            return;
          }
          handleAddFilter();
        }}
        submitText={filter.isUserSubmitted ? 'Update' : 'Add'}
        clearText="Clear"
        isSubmitDisabled={isAddDisabled}
      />
    </div>
  );
};

const NumberInput = ({
  value,
  ...restProps
}: NumberFormatProps<InputProps> & {
  value?: number | string | undefined;
}) => {
  const normalizedValue = value == null ? '' : value;

  return (
    <NumberFormat<InputProps>
      customInput={Input}
      size="sm"
      wrapperClassName={layoutStyles.numberField}
      thousandSeparator
      displayType="input"
      value={normalizedValue}
      {...restProps}
    />
  );
};
